import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const CHANNEL_CONFIG = {
  sms:    { label: 'SMS',    color: '#10b981', bg: '#d1fae5' },
  voice:  { label: 'Voice',  color: '#6366f1', bg: '#e0e7ff' },
  email:  { label: 'Email',  color: '#f59e0b', bg: '#fef3c7' },
  chat:   { label: 'Chat',   color: '#3b82f6', bg: '#dbeafe' },
  system: { label: 'System', color: '#6b7280', bg: '#f3f4f6' }
};

const DIRECTION_LABELS = {
  inbound: 'Inbound',
  outbound: 'Outbound'
};

function PatientTimeline({ practiceId, patientId }) {
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState(null);
  // Rows start collapsed: an email body is ~3KB of text and would bury the timeline.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const LIMIT = 30;

  const loadStats = useCallback(async () => {
    try {
      const res = await api.getPatientInteractionStats(practiceId, patientId);
      if (res.success) setStats(res.data);
    } catch (err) {
      console.error('Load stats error:', err);
    }
  }, [practiceId, patientId]);

  const loadTimeline = useCallback(async (currentOffset = 0, replace = true) => {
    try {
      setLoading(true);
      const params = { limit: LIMIT, offset: currentOffset };
      if (channelFilter) params.channel = channelFilter;
      const res = await api.getPatientTimeline(practiceId, patientId, params);
      if (res.success) {
        if (replace) {
          setInteractions(res.data || []);
        } else {
          setInteractions(prev => [...prev, ...(res.data || [])]);
        }
        setTotal(res.pagination?.total || 0);
        setHasMore(res.pagination?.hasMore || false);
        setOffset(currentOffset + LIMIT);
      }
    } catch (err) {
      console.error('Load timeline error:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, patientId, channelFilter]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    setInteractions([]);
    setOffset(0);
    setHasMore(true);
    loadTimeline(0, true);
  }, [loadTimeline]);

  const formatTime = (ts) => {
    if (!ts) return '';
    try {
      const d = new Date(ts.includes('T') ? ts : ts + 'Z');
      const now = new Date();
      const diffMs = now - d;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const date = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      if (diffDays === 0) return `Today ${date}, ${time}`;
      if (diffDays === 1) return `Yesterday ${date}, ${time}`;
      if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' }) + ` ${date}, ${time}`;
      return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) + `, ${time}`;
    } catch { return ts; }
  };

  const getSentimentBadge = (score) => {
    if (score == null) return null;
    const color = score > 0.3 ? '#10b981' : score < -0.3 ? '#ef4444' : '#f59e0b';
    const label = score > 0.3 ? 'Positive' : score < -0.3 ? 'Negative' : 'Neutral';
    return (
      <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '8px', background: color + '20', color, fontWeight: 500 }}>
        {label}
      </span>
    );
  };

  return (
    <div style={styles.container}>
      {/* Stats Bar */}
      {stats && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total}</div>
            <div style={styles.statLabel}>Total</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.inbound_count || 0}</div>
            <div style={styles.statLabel}>Inbound</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.outbound_count || 0}</div>
            <div style={styles.statLabel}>Outbound</div>
          </div>
          {stats.preferred_channel && (
            <div style={styles.statCard}>
              <div style={{ ...styles.statValue, fontSize: '14px', color: CHANNEL_CONFIG[stats.preferred_channel]?.color || '#374151' }}>
                {CHANNEL_CONFIG[stats.preferred_channel]?.label || stats.preferred_channel}
              </div>
              <div style={styles.statLabel}>Preferred</div>
            </div>
          )}
          {stats.avg_sentiment != null && (
            <div style={styles.statCard}>
              <div style={styles.statValue}>{stats.avg_sentiment}</div>
              <div style={styles.statLabel}>Avg Sentiment</div>
            </div>
          )}
        </div>
      )}

      {/* Channel Filter */}
      <div style={styles.filterRow}>
        <button
          onClick={() => setChannelFilter(null)}
          style={{ ...styles.filterBtn, ...(channelFilter === null ? styles.filterBtnActive : {}) }}
        >
          All ({total})
        </button>
        {Object.entries(CHANNEL_CONFIG).map(([key, cfg]) => {
          const count = stats?.by_channel?.[key] || 0;
          if (count === 0 && key === 'system') return null;
          return (
            <button
              key={key}
              onClick={() => setChannelFilter(key)}
              style={{
                ...styles.filterBtn,
                ...(channelFilter === key ? { background: cfg.color, color: '#fff', borderColor: cfg.color } : {})
              }}
            >
              {cfg.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      <div style={styles.timeline}>
        {loading && interactions.length === 0 ? (
          <div style={styles.empty}>Loading timeline...</div>
        ) : interactions.length === 0 ? (
          <div style={styles.empty}>No interactions recorded yet.</div>
        ) : (
          interactions.map((item, idx) => {
            const cfg = CHANNEL_CONFIG[item.channel] || CHANNEL_CONFIG.system;
            return (
              <div key={item.id || idx} style={styles.timelineItem}>
                <div style={styles.timelineDot}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.color }} />
                  {idx < interactions.length - 1 && <div style={styles.timelineLine} />}
                </div>
                <div style={styles.timelineContent}>
                  <div style={styles.timelineHeader}>
                    <span style={{ ...styles.channelBadge, background: cfg.bg, color: cfg.color }}>
                      {cfg.label}
                    </span>
                    {item.direction && (
                      <span style={styles.directionBadge}>
                        {item.direction === 'inbound' ? '\u2190' : '\u2192'} {DIRECTION_LABELS[item.direction]}
                      </span>
                    )}
                    {item.status && (
                      <span style={{
                        ...styles.statusBadge,
                        color: item.status === 'failed' ? '#ef4444' : item.status === 'delivered' ? '#10b981' : '#6b7280'
                      }}>
                        {item.status}
                      </span>
                    )}
                    {getSentimentBadge(item.sentiment_score)}
                    {item.ai_involved ? (
                      <span style={styles.aiBadge}>AI</span>
                    ) : null}
                  </div>
                  <div style={styles.summary}>{item.summary || item.interaction_type}</div>
                  {/* What the patient actually received. The summary alone cannot
                      distinguish a paperwork reminder from a telehealth join link —
                      both are "before appointment" messages — so staff had no way to
                      tell two sends apart without decrypting the bodies by hand. */}
                  {item.body_preview && (
                    <div style={styles.messagePreview}>
                      {expandedIds.has(item.id) && item.body
                        ? <span style={styles.messageFull}>{item.body}</span>
                        : item.body_preview}
                      {item.body && item.body !== item.body_preview && (
                        <button
                          type="button"
                          style={styles.expandButton}
                          onClick={() => setExpandedIds(prev => {
                            const next = new Set(prev);
                            next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                            return next;
                          })}
                        >
                          {expandedIds.has(item.id) ? 'Show less' : 'Show full message'}
                        </button>
                      )}
                    </div>
                  )}
                  {item.duration_seconds != null && (
                    <span style={styles.duration}>
                      {Math.floor(item.duration_seconds / 60)}m {item.duration_seconds % 60}s
                    </span>
                  )}
                  <div style={styles.timestamp}>{formatTime(item.occurred_at)}</div>
                </div>
              </div>
            );
          })
        )}
        {hasMore && !loading && (
          <button onClick={() => loadTimeline(offset, false)} style={styles.loadMoreBtn}>
            Load More
          </button>
        )}
        {loading && interactions.length > 0 && (
          <div style={styles.empty}>Loading...</div>
        )}
      </div>
    </div>
  );
}

const styles = {
  messagePreview: { fontSize: '12px', color: '#6b7280', marginTop: '4px', lineHeight: 1.5, background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: '4px', padding: '6px 8px' },
  messageFull: { whiteSpace: 'pre-wrap' },
  expandButton: { marginLeft: '6px', padding: 0, border: 'none', background: 'none', color: '#4f46e5', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' },
  container: { display: 'flex', flexDirection: 'column', gap: '16px' },
  statsRow: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  statCard: {
    flex: '1 1 80px', minWidth: '80px', background: '#f9fafb', borderRadius: '8px',
    padding: '12px 16px', textAlign: 'center'
  },
  statValue: { fontSize: '20px', fontWeight: '700', color: '#111827' },
  statLabel: { fontSize: '11px', color: '#6b7280', marginTop: '2px', textTransform: 'uppercase' },
  filterRow: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  filterBtn: {
    padding: '6px 12px', border: '1px solid #e5e7eb', borderRadius: '16px',
    background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer'
  },
  filterBtnActive: { background: '#374151', color: '#fff', borderColor: '#374151' },
  timeline: { display: 'flex', flexDirection: 'column' },
  timelineItem: { display: 'flex', gap: '12px', minHeight: '48px' },
  timelineDot: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    paddingTop: '4px', width: '20px', flexShrink: 0
  },
  timelineLine: { width: '2px', flex: 1, background: '#e5e7eb', marginTop: '4px' },
  timelineContent: {
    flex: 1, paddingBottom: '16px', borderBottom: '1px solid #f3f4f6'
  },
  timelineHeader: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  channelBadge: {
    padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
  },
  directionBadge: { fontSize: '11px', color: '#6b7280' },
  statusBadge: { fontSize: '11px', fontWeight: '500' },
  aiBadge: {
    fontSize: '10px', padding: '1px 6px', borderRadius: '8px',
    background: '#f3e8ff', color: '#7c3aed', fontWeight: 600
  },
  summary: { fontSize: '13px', color: '#374151', marginTop: '4px' },
  duration: { fontSize: '12px', color: '#9ca3af' },
  timestamp: { fontSize: '11px', color: '#9ca3af', marginTop: '2px' },
  empty: { textAlign: 'center', padding: '24px', color: '#9ca3af', fontSize: '13px' },
  loadMoreBtn: {
    margin: '12px auto', padding: '8px 20px', border: '1px solid #d1d5db',
    borderRadius: '6px', background: '#fff', color: '#374151', fontSize: '13px',
    cursor: 'pointer'
  }
};

export default PatientTimeline;
